import { useCallback, useEffect, useState } from "react";
import { fetchPendingRequests } from "../api/requestsAdmin";
import { usePolling } from "./usePolling";

const CountChangedEvent = "aia:assigned-request-count";

/**
 * Tells everything showing the number of requests waiting for the signed-in approver (the My Requests tab's badge) that it has
 * changed, so a page that has just loaded or decided those requests need not wait for the badge's own refresh.
 *
 * @param count - How many requests are now assigned to the signed-in person for a decision.
 */
export function publishAssignedRequestCount(count: number): void {
  window.dispatchEvent(new CustomEvent<number>(CountChangedEvent, { detail: count }));
}

/**
 * The number of requests assigned to the signed-in person for a decision, kept fresh on the polling schedule and whenever a page
 * publishes a new count. Zero while loading, when `enabled` is false, or when the count cannot be read.
 *
 * @param enabled - Whether the person is an approver; nothing is fetched otherwise.
 * @returns The number of requests waiting for them.
 */
export function useAssignedRequestCount(enabled: boolean): number {
  const [count, setCount] = useState(0);

  const refresh = useCallback(async () => {
    const result = await fetchPendingRequests(false, true);
    if (result.kind === "ok" && Array.isArray(result.response)) {
      setCount(result.response.length);
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      void refresh();
    } else {
      setCount(0);
    }
  }, [enabled, refresh]);

  useEffect(() => {
    const onChanged = (event: Event): void => setCount((event as CustomEvent<number>).detail);
    window.addEventListener(CountChangedEvent, onChanged);
    return () => window.removeEventListener(CountChangedEvent, onChanged);
  }, []);

  usePolling(refresh, enabled);
  return count;
}
